/**
 * SSML 辞書マスタ管理画面 (SBI-02)。
 *
 * 「変換元 → 置換後」の辞書を **言語別** に保守する GUI。ControlPanel API の
 * `/ssml-dictionary` エンドポイント群を利用する。
 *
 * 辞書は 4 種類ある (`dictionary_type`)。
 *   - `reading` (読み仮名辞典): 漢字の誤読を直す。置換後はひらがな / カタカナ。
 *   - `accent`  (アクセント辞典): アクセント位置を直す。置換後は全角カタカナ + アクセント核 `'`。
 *   - `text`    (テキスト編集): 読点を足すなど、文面そのものを書き換える。置換後は自由なテキスト。
 *   - `ssml`    (SSML構文): タグで間や抑揚を指定する。置換後は SSML の断片 (`<speak>` は含めない)。
 *
 * ⚠️ 本画面は **辞書の保守のみ**。放送文への適用は UpdateContents 側 (別 SBI) の責務で、
 *    ここで保存しても放送に反映されるのは適用側の実装後になる。
 *
 * 画面構成 (放送地点マスタ `BroadcastPointsPage` に合わせた構成):
 *   - 上: 操作バー (「＋ 新規作成」 + 再読み込み)。
 *   - 下: 種別ごとの辞書一覧を **常に 4 つ** 縦に並べる (アクセント → 読み仮名 → テキスト編集 → SSML構文の順。`points-table`)。
 *         各表の並び順は「置換後」のあいうえお順。
 *         行の「編集」でモーダルを開き、「削除」は確認ダイアログを経る。
 *   - 新規作成 / 編集は `SsmlDictionaryFormModal` (モーダル) で行う。
 *
 * 保存の考え方 (詳細はモーダル側):
 *   - 新規は POST (同一 `(language_code, source_text)` は上書き = 種別が違っても重複扱い)。
 *   - 編集は PUT。`language_code` / `source_text` はキーなので変更できない。
 *   - 保存時にサーバが返す `warnings` は「保存は成功したが確認してほしい点」として、
 *     登録 / 更新の完了ポップアップ (`NoticeDialog`) に、完了の文言と一緒に表示する。
 *   - 保存・削除の成功通知は出さない (放送地点マスタと同じ。モーダルが閉じて一覧が更新される /
 *   - 登録 / 更新 / 削除の完了は、画面に埋め込まずポップアップ (`NoticeDialog`) で知らせる。
 *     文言は操作ごとに分ける (「〜を登録しました」「〜を更新しました」「〜を削除しました」)。
 *   - 見出し行 (タイトルと説明文) と操作バーはスクロールさせず、エラーと 3 つの表だけを
 *     スクロールさせる。変更履歴の登録失敗の警告は、見落とさないよう見出し行の直下 (固定側) に出す。
 *   - エラー (サーバの英語メッセージは日本語に変換) は、背景 + 枠付きのバナーで出す。
 *   - 各行の「▶ 試聴」で、その行の辞書を適用した音を聴ける (アクセント行は phoneme SSML、
 *     読み仮名行は置換後の読み)。押すたびに `POST /audio-preview` で合成する (キャッシュしない)。
 *     他の登録語がその行の読みを書き換えないよう、辞書は適用しない (`applyDictionary: false`)。
 */
import { Spinner } from './Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { BroadcastPointsClient, base64ToAudioUrl } from '../api/broadcastPoints';
import { ApiError } from '../api/common';
import { SsmlDictionaryClient } from '../api/ssmlDictionary';
import type {
  SsmlDictionaryEntry,
  SsmlDictionarySaveResult,
  SsmlDictionaryType,
} from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { PHRASE_TEMPLATE_LANGUAGE_OPTIONS } from '../data/phraseTemplates';
import {
  SSML_DICTIONARY_TYPE_DEFINITIONS,
  buildDictionaryPreviewInput,
  dictionaryTypeLabel,
} from '../data/ssmlDictionary';
import { ConfirmDialog } from './ConfirmDialog';
import { MasterHistoryLink } from './MasterHistoryLink';
import { NoticeDialog } from './NoticeDialog';
import { SsmlDictionaryFormModal } from './SsmlDictionaryFormModal';
import { buildSavedNoticeMessage, describeApiError } from './ssmlDictionaryMessages';
import { StatusBanner } from './StatusBanner';
import { ssmlDictionaryTarget } from '../pages/masterHistoryTarget';

export interface SsmlDictionaryScreenProps {
  config: AppConfig;
}

/** 一覧に表示する表の順序 (アクセント → 読み仮名 → テキスト編集 → SSML構文) */
const TABLE_TYPE_ORDER: readonly SsmlDictionaryType[] = ['accent', 'reading', 'text', 'ssml'];

/** 日本語の辞書順 (あいうえお順) 比較器。ひらがな / カタカナは同じ読みとして並ぶ。 */
const JA_COLLATOR = new Intl.Collator('ja');

/**
 * 並び替え用のキー。アクセント辞典の置換後に含まれるアクセント核 `'` は
 * 読みの順序に関係しないので取り除く (`ナカ'モズ` → `ナカモズ`)。
 */
function replacementSortKey(replacement: string): string {
  return replacement.replace(/'/g, '');
}

/** 置換後のあいうえお順 → 言語 → 変換元 の順で並べ替えた新しい配列を返す。 */
function sortByReplacement(entries: readonly SsmlDictionaryEntry[]): SsmlDictionaryEntry[] {
  return [...entries].sort(
    (a, b) =>
      JA_COLLATOR.compare(replacementSortKey(a.replacement), replacementSortKey(b.replacement)) ||
      a.language_code.localeCompare(b.language_code) ||
      JA_COLLATOR.compare(a.source_text, b.source_text),
  );
}

/** 開いているモーダル (null = 閉じている) */
type ModalState = { mode: 'create' } | { mode: 'edit'; entry: SsmlDictionaryEntry } | null;

/** 言語キーの表示名 (未知のキーはそのまま返す) */
function languageLabel(languageKey: string): string {
  return PHRASE_TEMPLATE_LANGUAGE_OPTIONS.find((o) => o.key === languageKey)?.label ?? languageKey;
}

interface SsmlDictionaryTableProps {
  type: SsmlDictionaryType;
  /** 表示する行 (呼び出し側で種別で絞り込み・並べ替え済み) */
  entries: readonly SsmlDictionaryEntry[];
  loading: boolean;
  /** 削除処理中は行の操作を無効にする */
  busy: boolean;
  /** 試聴の音声を生成中の行のキー (null = 生成中なし)。生成中は全行の試聴を無効にする */
  previewingKey: string | null;
  onPreview: (entry: SsmlDictionaryEntry) => void;
  onEdit: (entry: SsmlDictionaryEntry) => void;
  onDelete: (entry: SsmlDictionaryEntry) => void;
}

/** 行を一意に表すキー (`(language_code, source_text)` が辞書のキー) */
function entryKey(entry: SsmlDictionaryEntry): string {
  return `${entry.language_code}-${entry.source_text}`;
}

/** 種別ごとの辞書一覧テーブル (見出し + 件数 + 表)。 */
function SsmlDictionaryTable(props: SsmlDictionaryTableProps): React.JSX.Element {
  const { type, entries, loading, busy, previewingKey, onPreview, onEdit, onDelete } = props;
  const label = SSML_DICTIONARY_TYPE_DEFINITIONS[type].label;

  return (
    <div className="ssml-dictionary-group" data-testid={`ssml-dictionary-group-${type}`}>
      <h3 data-testid={`ssml-dictionary-group-title-${type}`}>
        {label}{' '}
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : (
          `(${entries.length} 件)`
        )}
      </h3>
      <div className="table-wrapper">
        <table
          className="points-table ssml-dictionary-table"
          data-testid={`ssml-dictionary-table-${type}`}
        >
          {/* 3 つの表で列位置を揃えるため、列幅は固定レイアウト + 共通の colgroup で定義する */}
          <colgroup data-testid={`ssml-dictionary-colgroup-${type}`}>
            <col className="ssml-dictionary-col-language" />
            <col className="ssml-dictionary-col-source" />
            <col className="ssml-dictionary-col-replacement" />
            <col className="ssml-dictionary-col-note" />
            <col className="ssml-dictionary-col-enabled" />
            <col className="ssml-dictionary-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th>言語</th>
              <th>変換元</th>
              <th>置換後</th>
              <th>補足</th>
              <th>有効</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="empty-state"
                  data-testid={`ssml-dictionary-empty-${type}`}
                >
                  登録された辞書はありません
                </td>
              </tr>
            ) : null}
            {entries.map((entry) => (
              <tr
                key={entryKey(entry)}
                data-testid={`ssml-dictionary-row-${entry.language_code}-${entry.source_text}`}
                style={{ opacity: entry.enabled ? 1 : 0.55 }}
              >
                <td>{languageLabel(entry.language_code)}</td>
                <td>{entry.source_text}</td>
                <td>{entry.replacement}</td>
                <td>{entry.note ?? ''}</td>
                <td>{entry.enabled ? '有効' : '無効'}</td>
                <td className="row-actions">
                  <button
                    type="button"
                    onClick={() => onPreview(entry)}
                    disabled={busy || previewingKey !== null}
                    data-testid={`ssml-dictionary-row-preview-${entry.language_code}-${entry.source_text}`}
                  >
                    {previewingKey === entryKey(entry) ? '生成中...' : '▶ 試聴'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(entry)}
                    disabled={busy}
                    data-testid={`ssml-dictionary-edit-${entry.language_code}-${entry.source_text}`}
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => onDelete(entry)}
                    disabled={busy}
                    data-testid={`ssml-dictionary-delete-${entry.language_code}-${entry.source_text}`}
                  >
                    削除
                  </button>
                  <MasterHistoryLink
                    target={ssmlDictionaryTarget(entry.language_code, entry.source_text)}
                    subject={entry.source_text}
                    testId={`ssml-dictionary-history-${entry.language_code}-${entry.source_text}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SsmlDictionaryScreen(props: SsmlDictionaryScreenProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('ssml-dictionary-history-warning');

  const apiClient = useMemo(
    () => new SsmlDictionaryClient({ getIdToken, config }),
    [getIdToken, config],
  );
  /** 試聴用 (`POST /audio-preview`)。放送地点 API のクライアントを流用する */
  const previewClient = useMemo(
    () => new BroadcastPointsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [entries, setEntries] = useState<SsmlDictionaryEntry[]>([]);
  const [modal, setModal] = useState<ModalState>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 削除確認ダイアログの対象 */
  const [deleteTarget, setDeleteTarget] = useState<SsmlDictionaryEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** 登録 / 更新 / 削除の完了を知らせるポップアップの本文 (null = 表示しない)。登録 / 更新では警告も含む */
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  /** 試聴の音声を生成中の行のキー */
  const [previewingKey, setPreviewingKey] = useState<string | null>(null);
  /** 再生中の音声 (新しく再生するとき / 画面を離れるときに止めて URL を解放する) */
  const playingRef = useRef<{ audio: HTMLAudioElement; url: string } | null>(null);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしない防御) */
  const requestIdRef = useRef(0);

  /** 辞書一覧を取得して state に反映する。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setEntries(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(describeApiError(err).message);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  /** 種別ごとに振り分け、各表を置換後のあいうえお順に並べる。 */
  const entriesByType = useMemo(() => {
    const result: Record<SsmlDictionaryType, SsmlDictionaryEntry[]> = {
      accent: [],
      reading: [],
      text: [],
      ssml: [],
    };
    for (const type of TABLE_TYPE_ORDER) {
      result[type] = sortByReplacement(entries.filter((e) => e.dictionary_type === type));
    }
    return result;
  }, [entries]);

  /** 直前のエラー / 完了通知の表示を消す (次の操作に古い表示を持ち越さない)。 */
  const clearMessages = useCallback((): void => {
    setErrorMessage(null);
    setNoticeMessage(null);
  }, []);

  /** 再生中の音声を止めて、object URL を解放する。 */
  const stopPlaying = useCallback((): void => {
    const playing = playingRef.current;
    if (!playing) return;
    playing.audio.pause();
    URL.revokeObjectURL(playing.url);
    playingRef.current = null;
  }, []);

  useEffect(() => stopPlaying, [stopPlaying]);

  /**
   * 行の「▶ 試聴」。その行の辞書を適用した音を合成して再生する (押すたびに合成し、キャッシュしない)。
   *   - アクセント: `<phoneme ... ph="置換後">変換元</phoneme>` の SSML (アクセント適用済みの音)
   *   - 読み仮名: 置換後の読み (読み仮名で置換済みの音)
   * 辞書は適用しない: 他の登録語が、この行の読みを書き換えないようにするため。
   */
  const handlePreview = useCallback(
    async (entry: SsmlDictionaryEntry): Promise<void> => {
      const input = buildDictionaryPreviewInput(
        entry.dictionary_type,
        entry.source_text,
        entry.replacement,
      );
      if (input === null) return;
      stopPlaying();
      clearMessages();
      setPreviewingKey(entryKey(entry));
      try {
        const res = await previewClient.previewAudio({
          ...input,
          language: entry.language_code,
          applyDictionary: false,
        });
        const url = base64ToAudioUrl(res.content_base64, res.content_type);
        const audio = new Audio(url);
        playingRef.current = { audio, url };
        await audio.play();
      } catch (err) {
        stopPlaying();
        setErrorMessage(describeApiError(err).message);
      } finally {
        setPreviewingKey(null);
      }
    },
    [clearMessages, previewClient, stopPlaying],
  );

  const handleCreate = useCallback((): void => {
    clearMessages();
    setModal({ mode: 'create' });
  }, [clearMessages]);

  const handleEdit = useCallback(
    (entry: SsmlDictionaryEntry): void => {
      clearMessages();
      setModal({ mode: 'edit', entry });
    },
    [clearMessages],
  );

  /** モーダルでの保存成功: 完了ポップアップ (警告があれば同じポップアップに併記) を出し、モーダルを閉じて再取得する。 */
  const handleSaved = useCallback(
    (result: SsmlDictionarySaveResult): void => {
      const action = modal?.mode === 'edit' ? '更新' : '登録';
      setNoticeMessage(buildSavedNoticeMessage(action, result.source_text, result.warnings ?? []));
      // モーダルはここで閉じるので、履歴の警告はモーダルではなく画面側に出す
      reportHistory(result);
      setModal(null);
      void reload();
    },
    [modal, reload, reportHistory],
  );

  /** 編集中に他者が削除していた: お知らせを閉じた後、モーダルを閉じて一覧を取り直す (Issue #765)。 */
  const handleDeletedByOthers = useCallback((): void => {
    setModal(null);
    void reload();
  }, [reload]);

  /** 削除確認ダイアログで確定されたときの削除処理。 */
  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    clearMessages();
    try {
      const removed = await apiClient.remove(deleteTarget.language_code, deleteTarget.source_text);
      reportHistory(removed);
      await reload();
      setNoticeMessage(`「${deleteTarget.source_text}」を削除しました`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
        await reload();
      } else {
        setErrorMessage(describeApiError(err).message);
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [apiClient, clearMessages, deleteTarget, reload, reportHistory]);

  return (
    <div className="ssml-dictionary-page">
      <section className="section" aria-label="SSML辞書" data-testid="ssml-dictionary-screen">
        {/* 操作バー: 放送地点マスタの `RouteSelector` と同じ並び (新規作成 + 再読み込み) */}
        <div className="route-selector" data-testid="ssml-dictionary-toolbar">
          <button
            type="button"
            className="primary create-button"
            data-testid="ssml-dictionary-create"
            onClick={handleCreate}
          >
            ＋ 新規作成
          </button>
          <div className="actions">
            <button
              type="button"
              data-testid="ssml-dictionary-reload"
              onClick={() => void reload()}
              disabled={loading}
            >
              再読み込み
            </button>
          </div>
        </div>
      </section>

      <section
        className="section ssml-dictionary-list-section"
        data-testid="ssml-dictionary-list-section"
      >
        {/* 見出し行 (スクロールしない): 見出しと説明文を固定し、本文だけをスクロールさせる */}
        <header className="ssml-dictionary-list-header" data-testid="ssml-dictionary-list-header">
          <h2>SSML辞書一覧</h2>

          {historyWarning}

          <p className="ssml-dictionary-lead">
            放送文中の語句を、正しい読み・アクセントに置き換える辞書を<strong>言語別</strong>
            に登録します。同じ言語で同じ変換元は、種別が違っても<strong>1件</strong>
            として扱われます(上書き)。
          </p>
        </header>

        {/* 本文 (スクロール対象): エラー / 3 つの表 */}
        <div className="ssml-dictionary-list-body" data-testid="ssml-dictionary-list-body">
          {errorMessage ? (
            <StatusBanner
              variant="error"
              onDismiss={() => setErrorMessage(null)}
              data-testid="ssml-dictionary-error"
            >
              {errorMessage}
            </StatusBanner>
          ) : null}

          {TABLE_TYPE_ORDER.map((type) => (
            <SsmlDictionaryTable
              key={type}
              type={type}
              entries={entriesByType[type]}
              loading={loading}
              busy={deleting}
              previewingKey={previewingKey}
              onPreview={(entry) => void handlePreview(entry)}
              onEdit={handleEdit}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      </section>

      {modal ? (
        <SsmlDictionaryFormModal
          mode={modal.mode}
          entry={modal.mode === 'edit' ? modal.entry : null}
          existingEntries={entries}
          apiClient={apiClient}
          previewClient={previewClient}
          onSaved={handleSaved}
          onDeletedByOthers={handleDeletedByOthers}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="辞書の削除"
        message={
          deleteTarget
            ? `辞書 "${deleteTarget.source_text}" を削除します。よろしいですか？\n(${languageLabel(deleteTarget.language_code)} / ${dictionaryTypeLabel(deleteTarget.dictionary_type)})`
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* 登録 / 更新 / 削除の完了は、画面に埋め込まずポップアップで知らせる */}
      <NoticeDialog
        open={noticeMessage !== null}
        message={noticeMessage ?? ''}
        onClose={() => setNoticeMessage(null)}
      />
    </div>
  );
}
